// Hell oder dunkel: der Schalter im Header (app/views/layouts/application.html.erb).
//
// Die Farben selbst entscheidet das CSS -- jede Rolle in styles/tokens.css
// trägt per light-dark() beide Werte, und `color-scheme` am <html> wählt.
// Ohne Cookie steht dort `light dark`, also die Systemeinstellung; mit Cookie
// nagelt der Server `data-theme` fest, noch bevor hier eine Zeile läuft. Das
// Skript tut deshalb nur zwei Dinge: es schreibt das Cookie, wenn jemand
// drückt, und es hält den Schalter (aria-pressed, Symbol) mit dem Design in
// Deckung, das gerade tatsächlich zu sehen ist.
//
// Die Entscheidungen sind reine Funktionen, damit vitest sie ohne DOM prüft
// (`environment: node`); das Anhängen an die Seite entscheidet nichts.

export type Theme = 'light' | 'dark'

const THEMES: readonly Theme[] = ['light', 'dark']

function isTheme(value: unknown): value is Theme {
  return THEMES.includes(value as Theme)
}

/** What the page actually shows: the pinned design if there is one, else the system's. */
export function effectiveTheme(pinned: string | null | undefined, systemDark: boolean): Theme {
  if (isTheme(pinned)) return pinned
  return systemDark ? 'dark' : 'light'
}

export function oppositeTheme(theme: Theme): Theme {
  return theme === 'dark' ? 'light' : 'dark'
}

/** A year, every page, first-party only. The server reads it as `cookies[:theme]`. */
export function themeCookie(theme: Theme): string {
  return `theme=${theme}; Path=/; Max-Age=31536000; SameSite=Lax`
}

export function startThemeToggle(doc: Document = document): void {
  const root = doc.documentElement
  // Eingeloggt gibt es den Schalter zweimal: als Symbol in der Leiste und
  // als Zeile im Kontomenü (shared/_theme_toggle.html.erb). Beide schalten
  // dasselbe, beide zeigen dasselbe.
  const toggles = Array.from(doc.querySelectorAll<HTMLButtonElement>('.theme-toggle'))
  if (toggles.length === 0) return

  const systemDark = window.matchMedia('(prefers-color-scheme: dark)')
  const pinned = (): string | null => root.getAttribute('data-theme')

  // `data-theme-effective` is what the CSS reads for the icon, so the
  // attribute the server wrote (the pin) is never confused with what is shown.
  const reflect = (): void => {
    const shown = effectiveTheme(pinned(), systemDark.matches)
    root.setAttribute('data-theme-effective', shown)
    for (const toggle of toggles) toggle.setAttribute('aria-pressed', String(shown === 'dark'))
  }

  for (const toggle of toggles) {
    toggle.addEventListener('click', () => {
      const next = oppositeTheme(effectiveTheme(pinned(), systemDark.matches))
      root.setAttribute('data-theme', next)
      doc.cookie = themeCookie(next)
      reflect()
    })
  }

  systemDark.addEventListener('change', reflect)
  reflect()
}
